import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Activity, Brain, Target, CheckCircle2, Radar, Zap,
  Bell, Eye, RefreshCw, Filter, ExternalLink, Wifi, WifiOff,
  AlertTriangle, TrendingUp, Sparkles, FileText, Building2, Mail, Calendar,
} from 'lucide-react';
import { listFeed, getFeedStats, markAllFeedRead } from '../lib/api';
import { useAgentFeed } from '../hooks/useAgentFeed';

const CATEGORY_META = {
  sense: { label: 'SENSE', icon: Radar, color: 'text-blue-400' },
  think: { label: 'THINK', icon: Brain, color: 'text-purple-400' },
  act:   { label: 'ACT',   icon: Zap,   color: 'text-emerald-400' },
  identity: { label: 'IDENTITY', icon: Sparkles, color: 'text-amber-400' },
  other: { label: 'OTHER', icon: Activity, color: 'text-muted-foreground' },
};

const PRIORITY_COLORS = {
  critical: 'bg-red-500/15 text-red-300 border-red-500/40',
  high:     'bg-amber-500/15 text-amber-300 border-amber-500/40',
  medium:   'bg-blue-500/15 text-blue-300 border-blue-500/40',
  low:      'bg-muted text-muted-foreground',
};

const EVENT_ICONS = {
  'signal.discovered': Radar,
  'candidate.created': Target,
  'candidate.hot': TrendingUp,
  'tender.new': FileText,
  'tender.deadline_soon': Calendar,
  'swz.changed': AlertTriangle,
  'consilium.done': Brain,
  'redflag.detected': AlertTriangle,
  'outreach.sent': Mail,
  'agent.decision': Sparkles,
  'agent.briefing': Sparkles,
};

function timeAgo(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'przed chwilą';
    if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h temu`;
    return `${Math.floor(diff / 86400)} dni temu`;
  } catch { return ts; }
}

function FeedItem({ item, onClick, clickable }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  const Icon = EVENT_ICONS[item.event_type] || meta.icon;

  return (
    <Card
      className={`hover:border-primary/40 transition ${clickable ? 'cursor-pointer' : ''} ${!item.read ? 'border-l-4 border-l-primary/60' : ''}`}
      onClick={clickable ? onClick : undefined}
      title={clickable ? 'Kliknij aby zobaczyć szczegóły' : undefined}
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-muted/50 ${meta.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-sm line-clamp-2">{item.title}</div>
              <div className="flex items-center gap-1 shrink-0">
                <Badge variant="outline" className={PRIORITY_COLORS[item.priority]}>
                  {item.priority}
                </Badge>
              </div>
            </div>
            {item.summary && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.summary}</div>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className={`${meta.color} font-medium`}>{meta.label}</span>
              <span>{item.event_type}</span>
              {item.source && <span>· {item.source}</span>}
              <span className="ml-auto">{timeAgo(item.ts)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatsRow({ stats, connected }) {
  if (!stats) return null;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Badge variant="outline" className="text-base py-1 px-3">
        <Activity className="w-4 h-4 mr-1.5" /> {stats.total} eventów
      </Badge>
      <Badge variant="outline" className="text-base py-1 px-3 bg-primary/10 text-primary border-primary/30">
        <Eye className="w-4 h-4 mr-1.5" /> {stats.unread} nieprzeczytanych
      </Badge>
      {(stats.by_category || []).map(c => {
        const meta = CATEGORY_META[c._id] || CATEGORY_META.other;
        const Icon = meta.icon;
        return (
          <Badge key={c._id} variant="outline" className={meta.color}>
            <Icon className="w-3.5 h-3.5 mr-1" />
            {meta.label}: {c.count}
          </Badge>
        );
      })}
      <div className="ml-auto flex items-center gap-1 text-xs">
        {connected ? (
          <><Wifi className="w-3.5 h-3.5 text-emerald-400" /> live</>
        ) : (
          <><WifiOff className="w-3.5 h-3.5 text-muted-foreground" /> offline</>
        )}
      </div>
    </div>
  );
}

// Dokąd prowadzi kliknięcie feed itemu (null = niekclikalne)
function feedItemTarget(item) {
  const e = (item.event_type || '').toLowerCase();
  const d = item.data || {};
  if (e.startsWith('candidate')) {
    const cid = d.candidate_id || d.id;
    return cid ? `/pre-tender?candidate=${encodeURIComponent(cid)}` : '/pre-tender';
  }
  if (e.startsWith('signal')) return '/pre-tender';
  if (e.startsWith('tender') || e.startsWith('swz') || e.startsWith('consilium') ||
      e.startsWith('redflag') || e.startsWith('winrate')) {
    const tid = d.opportunity_id || d.source_id || d.opp_id || d.tender_id;
    return tid ? `/tenders/${encodeURIComponent(tid)}` : '/tenders';
  }
  if (e.startsWith('investment')) {
    const iid = d.opportunity_id || d.source_id || d.investment_id;
    return iid ? `/investments/${encodeURIComponent(iid)}` : '/investments';
  }
  return null;
}

export default function Feed({ lang, tenantId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { connected, lastEvent } = useAgentFeed();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        listFeed({ category: category || undefined, limit: 40, since_hours: 168 }),
        getFeedStats({ since_hours: 168 }),
      ]);
      setItems(list.items || []);
      setStats(st);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [category]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh na nowy event
  useEffect(() => {
    if (!lastEvent) return;
    // Instead of full refetch, prepend lastEvent to items (client-side)
    const optimistic = {
      id: lastEvent.id || String(Date.now()),
      event_type: lastEvent.event,
      title: lastEvent.data?.title || lastEvent.event,
      summary: JSON.stringify(lastEvent.data || {}).slice(0, 200),
      priority: 'medium',
      category: inferCategory(lastEvent.event),
      source: lastEvent.source || '',
      ts: new Date().toISOString(),
      read: false,
    };
    setItems((prev) => [optimistic, ...prev].slice(0, 50));
    // Refresh stats every 5 new events
    if (stats) setStats({ ...stats, total: stats.total + 1, unread: stats.unread + 1 });
  }, [lastEvent]);

  const handleMarkAllRead = async () => {
    await markAllFeedRead(category || undefined);
    await fetchData();
    toast.success('Oznaczono wszystkie jako przeczytane');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            Feed Adama
          </h1>
          <p className="text-sm text-muted-foreground">
            Wszystko co Adam wykrył / pomyślał / zrobił · real-time
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Odśwież
          </Button>
          <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
            <CheckCircle2 className="w-4 h-4 mr-1" /> Oznacz przeczytane
          </Button>
        </div>
      </div>

      <StatsRow stats={stats} connected={connected} />

      <Tabs value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? null : v)}>
        <TabsList className="grid grid-cols-5 max-w-xl">
          <TabsTrigger value="all">Wszystko</TabsTrigger>
          <TabsTrigger value="sense">Sense</TabsTrigger>
          <TabsTrigger value="think">Think</TabsTrigger>
          <TabsTrigger value="act">Act</TabsTrigger>
          <TabsTrigger value="identity">Identity</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
            <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Brak eventów w tej kategorii.</p>
            <p className="text-xs mt-1">Uruchom Adama (np. "Zbierz teraz" w Pre-Tender) aby pojawił się pierwszy event.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const target = feedItemTarget(item);
            return (
              <FeedItem
                key={item.id || item.ts}
                item={item}
                clickable={!!target}
                onClick={target ? () => navigate(target) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function inferCategory(event) {
  if (!event) return 'other';
  const e = event.toLowerCase();
  if (e.startsWith('signal') || e.startsWith('candidate') || e.startsWith('tender.new') ||
      e.startsWith('swz.changed') || e.startsWith('attachment') || e.startsWith('contractor'))
    return 'sense';
  if (e.startsWith('consilium') || e.startsWith('redflag') || e.startsWith('winrate') ||
      e.startsWith('swz.analyzed') || e.startsWith('agent.decision'))
    return 'think';
  if (e.startsWith('outreach') || e.startsWith('jedz') || e.startsWith('alert') ||
      e.startsWith('negotiation') || e.startsWith('deal') || e.startsWith('email') ||
      e.startsWith('call') || e.startsWith('tender.deadline'))
    return 'act';
  if (e.startsWith('agent.briefing')) return 'identity';
  return 'other';
}
